import type { DocSection } from '../types'
import { FIG_STATUS } from '../figures'

export const statusesSection: DocSection = {
  id: 'statuts',
  title: 'Statuts & disponibilité',
  icon: 'activity',
  accent: '#10B981',
  summary: 'Tous les statuts agent, ce qu\'ils signifient, et les passages automatiques (post-appel, inactivité, hors ligne).',
  minRole: 'agent',
  articles: [
    {
      id: 'liste',
      title: 'La liste complète des statuts',
      summary: 'Chaque statut, sa signification, et s\'il reçoit des appels.',
      icon: 'list',
      blocks: [
        { type: 'p', text: "Votre **statut** indique votre disponibilité. Vous le changez en haut de l'écran (sélecteur de statut). Seuls **Disponible** et **Travail post-appel** sont des « modes de travail » où le système vous attend en appel." },
        { type: 'figure', svg: FIG_STATUS, alt: 'Menu de sélection du statut agent', caption: 'Le sélecteur de statut, en haut à droite de l\'écran.' },
        { type: 'table', headers: ['Statut', 'Signification', 'Reçoit des appels ?', 'Choisi par'], rows: [
          ['🟢 **Disponible**', 'Prêt à recevoir/passer des appels', 'Oui', 'Vous'],
          ['📞 **En appel**', 'Communication en cours', 'Non', 'Système (automatique)'],
          ['📝 **Travail post-appel**', 'Saisie de fiche / notes après un appel', 'Non', 'Système (automatique)'],
          ['⛔ **Indisponible**', 'Ne pas déranger', 'Non', 'Vous'],
          ['☕ **Pause**', 'Pause courte', 'Non', 'Vous'],
          ['🍽️ **Déjeuner**', 'Pause repas', 'Non', 'Vous'],
          ['👥 **Réunion**', 'En réunion équipe', 'Non', 'Vous'],
          ['🎓 **Formation**', "Session d'apprentissage", 'Non', 'Vous'],
          ['📂 **Back office**', 'Tâches administratives', 'Non', 'Vous'],
          ['⚫ **Hors ligne**', 'Déconnecté', 'Non', 'Vous'],
        ] },
        { type: 'callout', variant: 'info', title: 'En appel & Post-appel sont automatiques', text: "Vous ne choisissez jamais **En appel** ni **Travail post-appel** : le système les pose tout seul quand vous décrochez et quand vous raccrochez." },
      ],
    },
    {
      id: 'transitions',
      title: 'Les passages automatiques',
      summary: 'Post-appel → Disponible → Indisponible → Hors ligne, sans rien faire.',
      icon: 'refresh',
      blocks: [
        { type: 'p', text: "Pour refléter la réalité (et ne pas fausser les statistiques de temps), PexiTalk fait évoluer votre statut tout seul dans certains cas." },
        { type: 'table', headers: ['Départ', 'Condition', 'Arrivée'], rows: [
          ['Travail post-appel', 'Au bout de **30 secondes**', 'Disponible'],
          ['Disponible', "**10 minutes** sans aucune activité (souris/clavier/navigation)", 'Indisponible'],
          ['Indisponible', '**30 minutes** de plus sans activité', 'Hors ligne'],
        ] },
        { type: 'callout', variant: 'tip', title: 'Comment rester Disponible', text: "Il suffit d'utiliser l'application (bouger la souris, naviguer, passer des appels). L'inactivité = aucune interaction du tout." },
      ],
    },
    {
      id: 'inactivite',
      title: 'Alerte d\'inactivité',
      summary: 'Le bandeau jaune « Inactivité » et ce qu\'il signifie.',
      icon: 'alert-triangle',
      blocks: [
        { type: 'p', text: "Quand vous êtes en **Disponible** (ou Post-appel) **plus de 30 secondes sans passer d'appel**, un bandeau jaune **Inactivité** s'affiche. C'est un rappel : prenez un appel ou changez de statut si vous faites autre chose." },
        { type: 'callout', variant: 'info', title: 'Côté superviseur', text: "Les responsables voient en temps réel les agents en alerte d'inactivité (bandeau et cloche). C'est un outil de pilotage, pas une sanction automatique." },
      ],
    },
    {
      id: 'offline',
      title: 'Passage Hors ligne et filet de sécurité',
      summary: 'Ce qui arrive à vos numéros quand vous passez Hors ligne.',
      icon: 'phone-off',
      blocks: [
        { type: 'p', text: "Quand vous passez **Hors ligne** (manuellement, en vous déconnectant, ou automatiquement) :" },
        { type: 'list', items: [
          "Les **numéros en main** (assignés, non traités) **retournent dans le pool** pour qu'un autre agent puisse les prendre.",
          "Vos **rappels personnels** restent à vous (ce sont des rendez-vous nominatifs) — un responsable peut les réaffecter.",
        ] },
        { type: 'h', text: 'Filet anti-oubli (côté serveur)' },
        { type: 'p', text: "Même si votre application est fermée, le serveur vous bascule en Hors ligne après une longue inactivité, pour ne pas vous compter « connecté » à tort :" },
        { type: 'kv', items: [
          { k: 'Statuts actifs (Disponible / Indisponible / Post-appel)', v: 'Hors ligne après **30 minutes** d\'inactivité réelle.' },
          { k: 'Statuts d\'absence (Pause / Déjeuner / Réunion / Formation / Back office)', v: 'Hors ligne après **4 heures** (filet anti-zombie plus long, car ces absences sont volontaires).' },
        ] },
        { type: 'callout', variant: 'warning', title: 'Déconnexion = Hors ligne immédiat', text: "Vous déconnecter vous passe en Hors ligne tout de suite et libère vos numéros. Pensez-y en fin de journée." },
      ],
    },
  ],
}
